// V3 Marketing Console — segments + campaigns + messages + automation
// esclusivamente su /api/v2/marketing-* (cap_marketing). Stessa console
// per ogni preset: il dominio è identico su tutti i verticali.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/session';

type Row = Record<string, any>;

const card: React.CSSProperties = { background: 'var(--sk-card, #fff)', borderRadius: 12, padding: 16, border: '1px solid var(--sk-border, #e5e7eb)' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--sk-border, #d1d5db)', fontSize: 14 };
const btn = (bg = '#111827'): React.CSSProperties => ({ padding: '7px 14px', borderRadius: 8, background: bg, color: '#fff', border: 0, cursor: 'pointer', fontSize: 13 });
const badge = (s: string): React.CSSProperties => ({
  padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
  background: s === 'sent' ? '#dcfce7' : s === 'draft' ? '#f3f4f6' : s === 'scheduled' ? '#dbeafe' : '#fee2e2',
  color: s === 'sent' ? '#166534' : s === 'draft' ? '#4b5563' : s === 'scheduled' ? '#1e40af' : '#991b1b',
});

export function V3MarketingPage() {
  const [segments, setSegments] = useState<Row[]>([]);
  const [campaigns, setCampaigns] = useState<Row[]>([]);
  const [messages, setMessages] = useState<Row[]>([]);
  const [automation, setAutomation] = useState<Row[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [segForm, setSegForm] = useState({ name: '', description: '' });
  const [campForm, setCampForm] = useState({ name: '', segment_id: '', channel: 'email', subject: '', body: '' });

  const load = useCallback(async () => {
    try {
      const [s, c, m, a] = await Promise.all([
        apiFetch('/api/v2/marketing-segments'), apiFetch('/api/v2/marketing-campaigns'),
        apiFetch('/api/v2/marketing-messages'), apiFetch('/api/v2/marketing-automation'),
      ]);
      setSegments(await s.json()); setCampaigns(await c.json());
      setMessages(await m.json()); setAutomation(await a.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const createSegment = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/marketing-segments', {
        method: 'POST',
        body: JSON.stringify({ name: segForm.name, description: segForm.description || null }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setSegForm({ name: '', description: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore segmento');
    } finally {
      setSaving(false);
    }
  };

  const createCampaign = async () => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch('/api/v2/marketing-campaigns', {
        method: 'POST',
        body: JSON.stringify({
          name: campForm.name, segment_id: campForm.segment_id ? Number(campForm.segment_id) : null,
          channel: campForm.channel, subject: campForm.subject || null, body: campForm.body || null,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      setCampForm({ name: '', segment_id: '', channel: 'email', subject: '', body: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore campagna');
    } finally {
      setSaving(false);
    }
  };

  const sendCampaign = async (id: number) => {
    setSaving(true); setError('');
    try {
      const res = await apiFetch(`/api/v2/marketing-campaigns/${id}/send`, { method: 'POST', body: '{}' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore invio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'minmax(300px, 380px) 1fr' }}>
      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Nuovo segmento</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <input style={input} placeholder="Nome segmento" value={segForm.name}
              onChange={(e) => setSegForm({ ...segForm, name: e.target.value })} />
            <input style={input} placeholder="Descrizione" value={segForm.description}
              onChange={(e) => setSegForm({ ...segForm, description: e.target.value })} />
            <button style={btn()} disabled={saving || !segForm.name} onClick={() => void createSegment()}>
              {saving ? 'Salvataggio…' : 'Crea segmento'}
            </button>
          </div>
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Nuova campagna</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <input style={input} placeholder="Nome campagna" value={campForm.name}
              onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} />
            <select style={input} value={campForm.segment_id}
              onChange={(e) => setCampForm({ ...campForm, segment_id: e.target.value })}>
              <option value="">Segmento (opzionale)…</option>
              {segments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select style={input} value={campForm.channel}
              onChange={(e) => setCampForm({ ...campForm, channel: e.target.value })}>
              <option value="email">email</option>
              <option value="sms">sms</option>
              <option value="push">push</option>
              <option value="whatsapp">whatsapp</option>
            </select>
            <input style={input} placeholder="Oggetto" value={campForm.subject}
              onChange={(e) => setCampForm({ ...campForm, subject: e.target.value })} />
            <textarea style={{ ...input, minHeight: 60 }} placeholder="Corpo messaggio" value={campForm.body}
              onChange={(e) => setCampForm({ ...campForm, body: e.target.value })} />
            <button style={btn('#0d6efd')} disabled={saving || !campForm.name} onClick={() => void createCampaign()}>
              {saving ? 'Salvataggio…' : 'Crea campagna (draft)'}
            </button>
          </div>
        </div>
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        <p className="sk-muted" style={{ fontSize: 12 }}>
          API: /api/v2 · {segments.length} segmenti · {campaigns.length} campagne · {messages.length} messaggi
        </p>
      </div>

      <div style={{ display: 'grid', gap: 20, alignContent: 'start' }}>
        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Campagne ({campaigns.length})</h4>
          {campaigns.length === 0 && <p className="sk-muted">Nessuna campagna.</p>}
          {campaigns.slice(0, 20).map((c) => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{c.name}</strong> · {c.channel}{c.segment_name ? ` · ${c.segment_name}` : ''}</span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={badge(c.status)}>{c.status}</span>
                <span style={{ color: '#6b7280' }}>{c.total_sent ?? 0}/{c.total_recipients ?? 0}</span>
                {c.status === 'draft' && (
                  <button style={btn('#166534')} disabled={saving} onClick={() => void sendCampaign(Number(c.id))}>Invia</button>
                )}
              </span>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Segmenti ({segments.length})</h4>
          {segments.length === 0 && <p className="sk-muted">Nessun segmento.</p>}
          {segments.map((s) => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{s.name}</strong>{s.description ? ` · ${s.description}` : ''}</span>
              <span style={{ color: '#6b7280' }}>{s.recipient_count} destinatari{s.is_active ? '' : ' · off'}</span>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Messaggi recenti</h4>
          {messages.length === 0 && <p className="sk-muted">Nessun messaggio.</p>}
          {messages.slice(0, 15).map((m) => (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span>{m.recipient || '—'} · {m.channel}</span>
              <span style={{ color: '#6b7280' }}>{m.status} · {String(m.sent_at || m.created_at).slice(0, 16).replace('T', ' ')}</span>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Automation rules ({automation.length})</h4>
          {automation.length === 0 && <p className="sk-muted">Nessuna regola.</p>}
          {automation.map((a) => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--sk-border, #eee)', fontSize: 13 }}>
              <span><strong>{a.name}</strong> · {a.trigger_type}</span>
              <span style={{ color: '#6b7280' }}>{a.is_active ? 'attiva' : 'off'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
